/* ImportScreenComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./ImportScreenRenderer");
const {
    templateFileName,
    baseFieldsData,
    getFieldsLabels,
    formatCustomFields,
    generateCSV,
    parseCSV,
    getListOfHeadersDefinitions,
} = require("./utils");

class ImportScreenComponent extends EventEmitter {
    constructor(
        importActionComponent,
        logger,
        CustomFields,
        userSession,
        io,
        ExtendedPropTypeEntityDef
    ) {
        super();
        this._logger = logger;
        this._CustomFieldsEntity = CustomFields;
        this._userSession = userSession;
        this._io = io;
        this._isOSMSolutionAvailable = () => importActionComponent.getOSMState();
        this._renderer = new Renderer(this);
        this._ExtendedPropTypeEntityDef = ExtendedPropTypeEntityDef;
        this._registerListeners();
        this._renderer.render();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    get logger() {
        return this._logger;
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._io = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ImportScreenComponent.shutdown() error.");
            }
        );
    }
    sendImportData(data, needSnapshot) {
        this._context.resolve({
            importData: data,
            needSnapshot: needSnapshot,
        });
    }
    sendImportError(err) {
        this._context.reject(err);
    }
    downloadCsvTemplate() {
        return Promise.all([this._getCustomFieldsDefs(), this._getExtendedPropTypeDefs()])
            .then(([customFieldsData, extendedPropTypeDefs]) => {
                const extendedPropsForImport = [];
                extendedPropTypeDefs.forEach((extendedPropTypeDef) => {
                    _.entries(extendedPropTypeDef.getDeviceImportFieldMap()).forEach(
                        ([key, value]) => {
                            extendedPropsForImport.push({
                                path: `edpValues.${extendedPropTypeDef.getExtensionType()}.${key}`,
                                title: `${extendedPropTypeDef.getExtensionType()}.${key}`,
                                type: value.dataType,
                            });
                        }
                    );
                });

                const OSMIsAvailable = this._isOSMSolutionAvailable();
                const fieldsData = []
                    .concat(baseFieldsData, customFieldsData, extendedPropsForImport)
                    .filter((col) => OSMIsAvailable || !col.OSMSpecific);

                const columnHeaders = getFieldsLabels(fieldsData);
                return generateCSV([], {columns: columnHeaders});
            })
            .then((templateContent) => {
                this._io.fileFactory.saveStringAsFile(templateContent, templateFileName);
                return Promise.resolve();
            });
    }
    parseData(file, type, needSnapshot) {
        new Promise((resolve, reject) => {
            const onloadHandler = (type) => (e) => {
                const data = e.target.result;
                if (type === "csv") {
                    this._parseCSVData(data).then(resolve, reject);
                } else if (type === "json") {
                    this._parseJson(data).then(resolve, reject);
                }
            };

            const onErrorHandler = (error) => {
                reject({
                    errorCode: "FileReadError",
                    errorMsg: "File reading error",
                    errorStack: error,
                });
            };

            if (type === "csv" || type === "json") {
                let fileReader = new FileReader();

                fileReader.onload = onloadHandler(type);
                fileReader.onerror = onErrorHandler;
                fileReader.readAsText(file);
            } else {
                reject({
                    errorCode: "UnknownImportFileType",
                    errorMsg: `Unknown file type used for import: ${type}`,
                });
            }
        })
            .then((data) => this.sendImportData(data, needSnapshot))
            .catch((error) => {
                this._logger.warn({error}, "ImportScreenComponent data parse error.");
                this.sendImportError(error);
            });
    }

    cancelAction(err) {
        this._context.reject(err);
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _getCustomFieldsDefs() {
        return this._CustomFieldsEntity
            .loadLabelValues(this._userSession.activeOrganization.id)
            .catch((error) => {
                this._logger.warn({error}, "Can not get Custom Fields definitions");
                return [];
            })
            .then((customFields) =>
                customFields
                    .filter(({systemOwned, enabled}) => !systemOwned && enabled !== false)
                    .sort(this._CustomFieldsEntity.getSortingComparator())
            )
            .then(formatCustomFields);
    }

    _getExtendedPropTypeDefs() {
        return this._ExtendedPropTypeEntityDef
            .loadExtendedPropTypes(this._userSession.activeOrganization.id, {enabled: {$ne: false}})
            .then((extendedPropTypes) => {
                return extendedPropTypes;
            })
            .catch(() => {
                return [];
            });
    }

    _parseJson(data) {
        return new Promise((resolve, reject) => {
            try {
                let parsedJson = JSON.parse(data);
                resolve(_.isArray(parsedJson) ? parsedJson : [parsedJson]);
            } catch (err) {
                if (err.name && err.name === "SyntaxError") {
                    reject({
                        errorCode: "SyntaxError",
                        errorMsg: "Unable to parse JSON file.",
                    });
                } else {
                    reject(err);
                }
            }
        });
    }

    _parseCSVData(fileData) {
        return Promise.all([
            this._getCustomFieldsDefs(),
            this._getExtendedPropTypeDefs(),
            parseCSV(fileData),
        ])
            .then(([customFieldsData, extendedPropTypeDefs, parsedData]) => {
                const extendedPropsForImport = [];
                extendedPropTypeDefs.forEach((extendedPropTypeDef) => {
                    _.entries(extendedPropTypeDef.getDeviceImportFieldMap()).forEach(
                        ([key, value], idx) => {
                            let dataType = value.dataType;

                            if (dataType === "enum") {
                                dataType = "string";
                            }

                            extendedPropsForImport.push({
                                path: `edpValues.${extendedPropTypeDef.getExtensionType()}.${key}`,
                                title: `${extendedPropTypeDef.getExtensionType()}.${key}`,
                                type: dataType,
                            });
                        }
                    );
                });

                const [headers, ...data] = parsedData;
                const OSMIsAvailable = this._isOSMSolutionAvailable();

                const fieldsData = []
                    .concat(baseFieldsData, customFieldsData, extendedPropsForImport)
                    .filter((col) => OSMIsAvailable || !col.OSMSpecific);

                const columnsDefinitions = getListOfHeadersDefinitions(fieldsData, headers);

                const cellsReducer = (device, cellValue, idx) => {
                    const colDef = columnsDefinitions[idx];
                    if (!colDef) {
                        return device;
                    }
                    if (colDef.isCustomField) {
                        return _.set(device, ["customFieldValues", colDef.title], cellValue);
                    }
                    let val;
                    switch (String(colDef.type).toLowerCase()) {
                        case "tags":
                            val = tagProcess(cellValue);
                            break;
                        case "number":
                            val = numberProcess(cellValue);
                            break;
                        case "boolean":
                            val = booleanProcess(cellValue);
                            break;
                        case "enum":
                            val = enumProcess(
                                cellValue,
                                colDef.typeInfo ? colDef.typeInfo : colDef
                            );
                            break;
                        case "string":
                            if (colDef.title === "Name") {
                                val = cellValue.trim();
                                break;
                            }
                            if (colDef.title === "Restore Commands") {
                                val = cellValue ? cellValue.split("\\n") : [];
                                break;
                            }
                        /* falls through */
                        default:
                            val = cellValue;
                            break;
                    }
                    return _.set(device, colDef.path, val);
                };

                return data.map((row) => row.reduce(cellsReducer, {}));

                function booleanProcess(value) {
                    return ["true", "yes", "on", "1"].includes(String(value).toLowerCase());
                }

                function numberProcess(value) {
                    return isNaN(value) && value !== null ? null : Number(value);
                }

                function tagProcess(value) {
                    return value
                        ? _.uniq(
                              value
                                  .split(",")
                                  .map((tag) => tag.trim())
                                  .filter(Boolean)
                          )
                        : [];
                }

                function enumProcess(value, typeInfo) {
                    const {ids = [], labels = []} = typeInfo || {};
                    const labelIndex = labels.indexOf(value);
                    return ids[labelIndex] || null;
                }
            })
            .catch((error) =>
                Promise.reject({
                    errorCode: "CsvImportError",
                    errorMsg: "Unable to import csv file.",
                    errorStack: error,
                })
            );
    }
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = ImportScreenComponent;
